import React, { useState } from "react";

import {
  IconButton,
  TableCell,
  TableRow,
  Typography,
  Box,
  Chip,
  Tooltip,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import AudiotrackOutlinedIcon from "@mui/icons-material/AudiotrackOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { Column } from "../../../../common/Table/types";
import { EpisodeStatus } from "../../../../Episode/enums/EpisodeStatus";
import RenameEpisodeDialog from "./RenameEpisodeDialog";
import { BRAND_COLORS } from "../../../../../constants";

interface TableListProps {
  columns: Column[];
  row: Record<string, any>;
}

const STATUS_CONFIG: Record<
  string,
  { label: string; color: string; bg: string }
> = {
  [EpisodeStatus.READY]: { label: "Ready", color: "#059669", bg: "#d1fae5" },
  [EpisodeStatus.PENDING]: {
    label: "Pending",
    color: "#d97706",
    bg: "#fef3c7",
  },
  [EpisodeStatus.FAILED]: { label: "Failed", color: "#dc2626", bg: "#fee2e2" },
};

function episodeFileTypeIconComponent(fileType: string | undefined) {
  const ft = fileType?.toLowerCase() ?? "";
  if (ft === "video") return VideocamOutlinedIcon;
  if (ft === "audio") return AudiotrackOutlinedIcon;
  return InsertDriveFileOutlinedIcon;
}

function EpisodeCell({
  column,
  row,
  value,
  onRename,
}: {
  column: Column;
  row: Record<string, any>;
  value: string;
  onRename: () => void;
}) {
  const navigate = useNavigate();
  const handleEpisodeClick = () => {
    if (row.status === EpisodeStatus.READY) {
      navigate(`/episodes/${row.id}`);
    }
  };

  if (column.id === "name") {
    const TypeIcon = episodeFileTypeIconComponent(row.file_type);
    return (
      <TableCell key={column.id} sx={{ py: 1.5, px: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "14px",
              backgroundColor: "rgba(37, 99, 235, 0.08)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              color: BRAND_COLORS.primary,
            }}
          >
            <TypeIcon sx={{ fontSize: 22 }} />
          </Box>
          <Typography
            onClick={handleEpisodeClick}
            sx={{
              fontSize: "13px",
              fontWeight: 600,
              color:
                row.status === EpisodeStatus.READY
                  ? BRAND_COLORS.primary
                  : "text.primary",
              cursor:
                row.status === EpisodeStatus.READY ? "pointer" : "default",
              "&:hover":
                row.status === EpisodeStatus.READY
                  ? { textDecoration: "underline" }
                  : {},
              lineHeight: 1.4,
            }}
          >
            {value}
          </Typography>
        </Box>
      </TableCell>
    );
  }

  if (column.id === "project") {
    return (
      <TableCell key={column.id} sx={{ py: 1.5, px: 2.5 }}>
        <Typography sx={{ fontSize: "13px", color: "text.secondary" }}>
          {value}
        </Typography>
      </TableCell>
    );
  }

  if (column.id === "upload") {
    return (
      <TableCell key={column.id} sx={{ py: 1.5, px: 2.5 }}>
        <Typography
          sx={{
            fontSize: "13px",
            color: "text.secondary",
            whiteSpace: "nowrap",
          }}
        >
          {value}
        </Typography>
      </TableCell>
    );
  }

  if (column.id === "file_type") {
    const isUnknown = value === "Unknown";
    return (
      <TableCell key={column.id} sx={{ py: 1.5, px: 2.5 }}>
        <Chip
          label={value}
          size="small"
          sx={{
            backgroundColor: isUnknown
              ? "rgba(107, 114, 128, 0.1)"
              : "rgba(37, 99, 235, 0.1)",
            color: isUnknown ? "#6b7280" : BRAND_COLORS.primary,
            fontWeight: 600,
            fontSize: "11px",
            height: 22,
            borderRadius: "6px",
            "& .MuiChip-label": { px: 1 },
          }}
        />
      </TableCell>
    );
  }

  if (column.id === "duration") {
    return (
      <TableCell key={column.id} sx={{ py: 1.5, px: 2.5 }}>
        <Typography
          sx={{
            fontSize: "13px",
            color: "text.primary",
            fontVariantNumeric: "tabular-nums",
            fontWeight: 500,
          }}
        >
          {value}
        </Typography>
      </TableCell>
    );
  }

  if (column.id === "status") {
    const config = STATUS_CONFIG[value] || {
      label: value,
      color: "#6b7280",
      bg: "#f3f4f6",
    };
    return (
      <TableCell key={column.id} sx={{ py: 1.5, px: 2.5 }}>
        <Chip
          label={config.label}
          size="small"
          sx={{
            backgroundColor: config.bg,
            color: config.color,
            fontWeight: 600,
            fontSize: "11px",
            height: 22,
            borderRadius: "6px",
            border: "none",
            "& .MuiChip-label": { px: 1.25 },
          }}
        />
      </TableCell>
    );
  }

  if (column.id === "view") {
    return (
      <TableCell key={column.id} sx={{ py: 1.5, px: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {row.status === EpisodeStatus.READY && (
            <Tooltip title="View episode">
              <IconButton
                size="small"
                onClick={() => navigate(`/episodes/${row.id}`)}
                sx={{
                  color: "text.secondary",
                  "&:hover": {
                    color: BRAND_COLORS.primary,
                    backgroundColor: "rgba(37, 99, 235, 0.06)",
                  },
                  borderRadius: "6px",
                  width: 30,
                  height: 30,
                }}
              >
                <VisibilityIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Rename">
            <IconButton
              size="small"
              onClick={onRename}
              sx={{
                color: "text.secondary",
                "&:hover": {
                  color: BRAND_COLORS.primary,
                  backgroundColor: "rgba(37, 99, 235, 0.06)",
                },
                borderRadius: "6px",
                width: 30,
                height: 30,
              }}
            >
              <DriveFileRenameOutlineIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </TableCell>
    );
  }

  return (
    <TableCell key={column.id} sx={{ py: 1.5, px: 2.5 }}>
      <Typography sx={{ fontSize: "13px", color: "text.primary" }}>
        {column.format && typeof value === "number"
          ? column.format(value)
          : value}
      </Typography>
    </TableCell>
  );
}

export default function TableList({ columns, row }: TableListProps) {
  const [renameOpen, setRenameOpen] = useState(false);
  const queryClient = useQueryClient();

  return (
    <>
      <TableRow
        hover
        sx={{
          "&:hover": { backgroundColor: "rgba(37, 99, 235, 0.02)" },
          "&:last-child td": { borderBottom: "none" },
          transition: "background-color 0.1s ease",
        }}
      >
        {columns.map((column: Column) => (
          <EpisodeCell
            key={column.id}
            column={column}
            row={row}
            value={row[column.id]}
            onRename={() => setRenameOpen(true)}
          />
        ))}
      </TableRow>

      <RenameEpisodeDialog
        open={renameOpen}
        handleClose={() => setRenameOpen(false)}
        episodeId={row.id}
        episodeName={row.name}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ["episodes"] });
          queryClient.invalidateQueries({ queryKey: ["dashboard-stats"] });
        }}
      />
    </>
  );
}
